import AppColors from 'config/AppColors';
import { Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import { CartesianGrid, Label, ReferenceArea, ReferenceDot, XAxis, YAxis } from 'recharts';

export type CalendarChartProps = {
  dayLabels: Dayjs[];
  dayRange?: [Dayjs, Dayjs];
  periodDots?: Dayjs[];
  drawHollowPeriodDots?: boolean;
  topLabels?: [Dayjs, string][];
  showMonthLabels?: boolean;
};

export default function useCalendarChart({
  dayLabels,
  dayRange,
  periodDots,
  drawHollowPeriodDots = false,
  topLabels,
  showMonthLabels = false,
}: CalendarChartProps) {
  // Calculate domain boundaries
  const { displayedMinDay, displayedMaxDay, domainMinDay, domainMaxDay } = useMemo(
    () => ({
      displayedMinDay: dayLabels[0],
      displayedMaxDay: dayLabels[dayLabels.length - 1],
      domainMinDay: dayRange ? dayRange[0] : dayLabels[0],
      domainMaxDay: dayRange ? dayRange[1] : dayLabels[dayLabels.length - 1],
    }),
    [dayLabels, dayRange],
  );

  // Generate days in domain
  const daysInDomain = useMemo(
    () => Array.from({ length: domainMaxDay.diff(domainMinDay, 'days') + 1 }, (_, i) => domainMinDay.add(i, 'days')),
    [domainMinDay, domainMaxDay],
  );

  // Calculate first days of months
  const firstDaysOfMonths = useMemo(() => {
    if (showMonthLabels) {
      return dayLabels.filter(day => day.date() === 1);
    }
    return [];
  }, [dayLabels, showMonthLabels]);

  // Calculate dimensions and ranges
  const dimensions = useMemo(() => {
    const yRange = 5;
    const simpleGraphHeight = 70;
    const topLabelsHeight = topLabels ? 24 : 0;
    const pixelsPerUnit = simpleGraphHeight / yRange;
    const graphHeight = simpleGraphHeight + (periodDots ? 10 : 0) + (showMonthLabels ? 15 : 0) + topLabelsHeight;
    const yAxisDomain = [
      (0 - (periodDots ? 10 : 0) - (showMonthLabels ? 15 : 0)) / pixelsPerUnit,
      topLabels ? 5 + topLabelsHeight / pixelsPerUnit : 5,
    ];

    return {
      yRange,
      simpleGraphHeight,
      topLabelsHeight,
      pixelsPerUnit,
      graphHeight,
      yAxisDomain,
    };
  }, [periodDots, showMonthLabels, topLabels]);

  const verticalGridLines = useMemo(() => {
    return Array.from({ length: daysInDomain.length }, (_, index) => index * 14);
  }, [daysInDomain]);

  // Caret down component
  const caretDown = useMemo(
    () =>
      ({ cx, cy }: { cx: number; cy: number }) => (
        <svg width={4} height={4} style={{ overflow: 'visible' }}>
          <polygon points={`${cx - 5},${cy - 2} ${cx + 5},${cy - 2} ${cx},${cy + 4}`} fill={AppColors.midGrey} />
        </svg>
      ),
    [],
  );

  // Generate chart building blocks
  const chartBuildingBlocks = useMemo(
    () => (
      <>
        <XAxis type="number" dataKey="dayIndex" domain={[0, domainMaxDay.diff(domainMinDay, 'days') + 1]} hide />
        <YAxis dataKey="dayValue" domain={dimensions.yAxisDomain} hide />
        <ReferenceArea
          key="daybar-background"
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={1}
          fill="#E7E7E7"
          fillOpacity={1}
          stroke={AppColors.backgroundGrey}
          strokeWidth={2}
          ifOverflow="visible"
        />
        <CartesianGrid
          horizontal={false}
          stroke={AppColors.backgroundGrey}
          strokeWidth={2}
          verticalPoints={verticalGridLines}
        />
        <ReferenceArea
          key="hide-grid-top"
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={5}
          y2={10}
          fill="white"
          fillOpacity={1}
          ifOverflow="visible"
        />
        <ReferenceArea
          key="hide-grid-bottom"
          x1={0}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={-10}
          fill="white"
          fillOpacity={1}
          ifOverflow="visible"
        />
        {daysInDomain.map((day, index) =>
          day.isoWeekday() < 6 ? (
            // using simple svg text to minimize react components and speed up rendering
            <text
              key={`daybar-text-${index}`}
              x={index * 14 + 7}
              textAnchor="middle"
              y={dimensions.simpleGraphHeight + dimensions.topLabelsHeight - 4}
              fill="#979797"
              style={{ fontSize: 8, color: '#979797' }}
            >
              {day.date()}
            </text>
          ) : (
            <ReferenceArea
              key={`daybar-${index}`}
              x1={index}
              x2={index + 1}
              y1={0}
              y2={1}
              fill="#979797"
              fillOpacity={1}
              stroke={AppColors.backgroundGrey}
              strokeWidth={2}
              ifOverflow="visible"
              label={<Label fontSize={8} value={day.date()} fill="white" />}
            />
          ),
        )}
        {periodDots?.map((day, index) => (
          <ReferenceDot
            key={`perioddot-${index}`}
            x={day.diff(domainMinDay, 'days') + 0.5}
            y={dimensions.yAxisDomain[0] / 2}
            r={3}
            fill={AppColors.colorMap.veryBad}
            fillOpacity={drawHollowPeriodDots ? 0 : 1}
            stroke={AppColors.colorMap.veryBad}
            strokeWidth={drawHollowPeriodDots ? 1 : 0}
            ifOverflow="visible"
          />
        ))}
        {topLabels?.map(([day, label], index) => (
          <ReferenceDot
            key={`toplabel-${index}`}
            x={day.diff(domainMinDay, 'days') + 0.5}
            y={dimensions.yAxisDomain[1] - 1.2}
            r={2}
            fill={AppColors.text.lightGray}
            fillOpacity={1}
            strokeWidth={0}
            shape={caretDown}
            label={<Label position="top" fontWeight={700} fontSize={13} value={label} fill={AppColors.text.discrete} />}
          />
        ))}
        {firstDaysOfMonths.map((day, index) => (
          <React.Fragment key={`month-${index}`}>
            <ReferenceArea
              x1={day.diff(domainMinDay, 'days')}
              x2={day.diff(domainMinDay, 'days') + 0.1}
              y1={0}
              y2={-2}
              fillOpacity={0}
              strokeWidth={2}
              stroke={AppColors.backgroundGrey}
              ifOverflow="visible"
            />
            <ReferenceArea
              x1={day.diff(domainMinDay, 'days')}
              x2={day.diff(domainMinDay, 'days')}
              y1={dimensions.yAxisDomain[0] + 0.5}
              y2={dimensions.yAxisDomain[0] + 0.5}
              fillOpacity={0}
              strokeWidth={0}
              label={
                <Label
                  position="right"
                  fontSize={13}
                  value={day.format('MMMM').charAt(0).toUpperCase() + day.format('MMMM').slice(1).toLowerCase()}
                  fill={AppColors.text.veryLightGray}
                />
              }
            />
          </React.Fragment>
        ))}
        <ReferenceArea
          key="gray-overlay-left"
          x1={0}
          x2={displayedMinDay.diff(domainMinDay, 'days')}
          y1={0}
          y2={5}
          fill={AppColors.backgroundGrey}
          fillOpacity={0.5}
          ifOverflow="visible"
        />
        <ReferenceArea
          key="gray-overlay-right"
          x1={displayedMaxDay.diff(domainMinDay, 'days') + 1}
          x2={domainMaxDay.diff(domainMinDay, 'days') + 1}
          y1={0}
          y2={5}
          fill={AppColors.backgroundGrey}
          fillOpacity={0.5}
          ifOverflow="visible"
        />
      </>
    ),
    [
      daysInDomain,
      domainMinDay,
      domainMaxDay,
      dimensions.yAxisDomain,
      periodDots,
      topLabels,
      firstDaysOfMonths,
      displayedMinDay,
      displayedMaxDay,
      caretDown,
      verticalGridLines,
      drawHollowPeriodDots,
    ],
  );

  return {
    graphHeight: dimensions.graphHeight,
    yRange: dimensions.yRange,
    simpleGraphHeight: dimensions.simpleGraphHeight,
    topLabelsHeight: dimensions.topLabelsHeight,
    pixelsPerUnit: dimensions.pixelsPerUnit,
    yAxisDomain: dimensions.yAxisDomain,
    domainMaxDay,
    domainMinDay,
    chartBuildingBlocks,
  };
}
